import { apiService } from './api';
import { ApiResponse, ImpersonationResponse, LoginRequest, LoginResponse, UserInfo } from '../types';

/**
 * Outcome of a token refresh. Tri-state so callers can tell a genuinely dead session apart from a
 * passing outage and STOP logging users out on the latter (the root cause of users being logged out
 * several times a day).
 * - 'refreshed': a new access token was stored (and the httpOnly refresh cookie rotated); proceed.
 * - 'invalid':   the session is definitively dead (HTTP 4xx other than 429). The caller should log
 *                out. Auth data has already been cleared.
 * - 'transient': a temporary failure (503 / other 5xx / 429 / network error) that survived bounded
 *                retries. The caller MUST keep the session (do NOT clearAuthData) — a later request
 *                can recover it.
 */
export type RefreshOutcome = 'refreshed' | 'invalid' | 'transient';

// Cross-tab Web Lock name that serialises refreshes (Fix 1). Only one holder at a time across every
// tab; the others double-check the freshness signal and reuse the just-refreshed session rather than
// racing a second POST.
const REFRESH_LOCK_NAME = 'dris-token-refresh';

// Custom header the backend requires on /auth/refresh. Its presence forces a CORS preflight that the
// strict origin allowlist blocks for forged cross-origin requests — a CSRF defense on top of SameSite.
const REFRESH_HEADER_NAME = 'X-DRIS-Refresh';

// In-memory ACCESS token (PR3). Deliberately NOT persisted to localStorage: a memory-only token closes
// the XSS token-exfiltration vector, and a page reload re-establishes it via the silent cookie refresh
// (see AuthContext.initializeAuth). The REFRESH token lives in an httpOnly cookie and is not JS-readable.
let accessToken: string | null = null;

// The admin's OWN access token, stashed while impersonating so we can restore it on exit. Non-null EXACTLY
// while an impersonation session is active — `isImpersonating()` keys on it. The stashed admin token is
// often already expired by exit time (15-min token, 30-min impersonation window), in which case the exit
// path refreshes off the admin's still-valid dris_rt cookie instead (see restoreAdminSession).
let impersonatorToken: string | null = null;

// Fallback single-flight for when navigator.locks is unavailable (older browsers / test env): coalesce
// concurrent in-process callers onto one in-flight refresh so we still fire at most one POST.
let refreshInFlight: Promise<RefreshOutcome> | null = null;

export class AuthService {
  // Legacy localStorage keys. Post-migration the app writes NO tokens here; these remain only so we can
  // (a) one-time bootstrap a pre-migration refresh token into the cookie and (b) purge any stragglers.
  private static readonly TOKEN_KEY = 'authToken';
  private static readonly REFRESH_TOKEN_KEY = 'refreshToken';
  private static readonly USER_KEY = 'userData';

  // Non-secret cross-flight/cross-tab freshness signal (replaces PR2's token-reading double-check — the
  // refresh token is no longer JS-readable). Written after every successful refresh/login; a queued
  // holder that finds it fresh skips a redundant network refresh. NO token is read for this check.
  private static readonly LAST_REFRESH_KEY = 'lastRefreshAt';
  private static readonly REFRESH_FRESH_WINDOW_MS = 5000;

  // Transient-refresh retry budget: up to 3 attempts with exponential backoff + jitter between them
  // (see refreshBackoffDelay), keeping the total wait under ~8s. `refreshBackoffBaseMs` is a test
  // seam — tests shrink it so the retry path runs fast; production keeps the real cadence.
  private static readonly REFRESH_MAX_ATTEMPTS = 3;
  static refreshBackoffBaseMs = 500;

  // Worst-case cap for the INITIAL silent refresh on app load (AuthContext.initializeAuth): a SINGLE
  // attempt with NO transient retries, so the load-time splash is bounded by ONE request timeout
  // instead of the full multi-attempt budget (at the 60s axios timeout, 3 attempts + backoff could
  // stack to ~3min behind a blank/splash screen). A transient outcome still KEEPS the session, so a
  // later request can silently recover it. Reactive (401) and proactive (near-expiry) refreshes keep
  // the full REFRESH_MAX_ATTEMPTS budget.
  static readonly INITIAL_REFRESH_MAX_ATTEMPTS = 1;

  /**
   * Login user with email and password
   */
  static async login(credentials: LoginRequest): Promise<LoginResponse | { requires2FA: boolean }> {
    const response = await apiService.post<LoginResponse & { requires2FA?: boolean }>('/auth/login', credentials);

    if (response.success && response.data) {
      // Check if 2FA is required
      if (response.data.requires2FA) {
        return { requires2FA: true };
      }

      // Access token → memory; the refresh token arrives as an httpOnly cookie (Set-Cookie), NOT stored
      // here. The body still carries a refreshToken during the transition — we deliberately ignore it.
      this.setToken(response.data.token);
      this.setUser(response.data.user);
      this.markRefreshFresh();

      return response.data as LoginResponse;
    } else {
      throw new Error(response.message || 'Login failed');
    }
  }

  /**
   * Adopt a session minted by a NON-password flow — the email-verification endpoints return the same
   * LoginResponse as /auth/login when THEY activated the account (and the server has already set the
   * httpOnly refresh cookie). Mirrors exactly what login()/verify2FA() do after a successful post:
   * access token → memory, user → session storage, refresh marked fresh.
   */
  static adoptSession(login: LoginResponse): void {
    this.setToken(login.token);
    this.setUser(login.user);
    this.markRefreshFresh();
  }

  /**
   * Verify 2FA code during login
   */
  static async verify2FA(email: string, password: string, code: string): Promise<LoginResponse> {
    const response = await apiService.post<LoginResponse>('/auth/verify-2fa', {
      email,
      password,
      code
    });

    if (response.success && response.data) {
      // Access token → memory; refresh token → httpOnly cookie (see login()).
      this.setToken(response.data.token);
      this.setUser(response.data.user);
      this.markRefreshFresh();

      return response.data;
    } else {
      throw new Error(response.message || '2FA verification failed');
    }
  }

  /**
   * Logout user, revoke refresh token, and clear stored data
   */
  static async logout(): Promise<void> {
    try {
      // Call logout endpoint if authenticated. withCredentials sends the httpOnly cookie the server
      // revokes + clears; a legacy body token (if any) is included so pre-migration sessions revoke too.
      if (this.getToken()) {
        const legacyToken = this.getRefreshToken();
        await apiService.post('/auth/logout', legacyToken ? { refreshToken: legacyToken } : {});
      }
    } catch {
      // Continue with local logout even if server call fails
    } finally {
      this.clearAuthData();
    }
  }

  /**
   * Get current user information
   */
  static async getCurrentUser(): Promise<UserInfo> {
    const response = await apiService.get<UserInfo>('/auth/me');

    if (response.success && response.data) {
      this.setUser(response.data);
      return response.data;
    } else {
      throw new Error(response.message || 'Failed to get user info');
    }
  }

  /**
   * True while an impersonation session is active (the admin's own token is stashed). Used to SUPPRESS
   * every token refresh while impersonating — a cookie refresh mints an ADMIN token and would silently
   * convert the impersonation session (invariant #4). The server also refuses such a refresh (C1); this
   * is the client half.
   */
  static isImpersonating(): boolean {
    return impersonatorToken !== null;
  }

  /**
   * Start impersonating a user (SuperAdmin only). Stashes the current admin token, then swaps the
   * in-memory access token to the returned short-lived impersonation token. Returns the server response
   * (target UserInfo + acting-admin email). Throws on failure (leaves the admin session untouched).
   */
  static async startImpersonation(userId: number, reason: string): Promise<ImpersonationResponse> {
    const response = await apiService.post<ImpersonationResponse>(`/auth/impersonate/${userId}`, { reason });
    if (response.success && response.data) {
      // Stash the admin token BEFORE swapping so exit can restore it (or refresh off the admin cookie).
      impersonatorToken = accessToken;
      this.setToken(response.data.token);
      return response.data;
    }
    throw new Error(response.message || 'Failed to start impersonation');
  }

  /**
   * Stop impersonating and restore the admin session. Order is load-bearing (C1): POST /stop with the
   * impersonation bearer still attached (the write-guard allow-lists it) to write the STOP audit row,
   * then DROP the impersonation bearer and restore the admin token — refreshing off the admin cookie if
   * the stashed admin token has since expired. Best-effort on the /stop call: an expired token 401s, which
   * must NOT block the local restore.
   */
  static async stopImpersonation(): Promise<void> {
    if (this.isImpersonating()) {
      try {
        await apiService.post('/auth/impersonate/stop', {});
      } catch {
        // Token may already be expired/denied — the audit row is best-effort; still restore locally.
      }
    }
    await this.restoreAdminSession();
  }

  /**
   * Restore the admin session after impersonation ends (explicit exit OR token expiry). DROPS the
   * impersonation bearer first (C1 ordering), then reinstates the stashed admin token; if that token is
   * absent/expired, refreshes bearer-less off the admin's dris_rt cookie. The server refuses a refresh
   * ONLY for imp=1 bearers, so this admin-recovery refresh always proceeds.
   */
  private static async restoreAdminSession(): Promise<void> {
    const stashed = impersonatorToken;
    // Drop the impersonation bearer BEFORE any refresh so the server never sees imp=1 on the recovery call.
    impersonatorToken = null;
    accessToken = stashed;

    if (!this.isAuthenticated()) {
      // Admin token gone/expired — mint a fresh one off the admin cookie.
      await this.refreshAccessToken();
    }
  }

  /**
   * Update the signed-in user's own name + mobile number (`PUT /auth/me/profile`). Returns the refreshed
   * `UserInfo` (same assembly as `/auth/me`) so the caller can hand it to `refreshUser` without a second
   * round-trip. Validation problems arrive as a rejected, enriched error carrying `errors[]`.
   */
  static async updateProfile(body: { firstName: string; lastName: string; phone?: string | null }): Promise<UserInfo> {
    const response = await apiService.put<UserInfo>('/auth/me/profile', body);
    if (response.success && response.data) {
      this.setUser(response.data);
      return response.data;
    }
    throw new Error(response.message || 'Failed to update profile');
  }

  /**
   * Change the signed-in user's password (`POST /auth/change-password`). A wrong current password is a
   * rejected error with `errorCode === 'INVALID_CURRENT_PASSWORD'`; a weak new one `'WEAK_PASSWORD'`.
   * On success the server keeps THIS browser's session and revokes every other one.
   */
  static async changePassword(currentPassword: string, newPassword: string): Promise<ApiResponse<boolean>> {
    return apiService.post<boolean>('/auth/change-password', { currentPassword, newPassword });
  }

  /**
   * Request a password-reset link (anonymous — no token required).
   *
   * Enumeration-safe: the backend returns one identical response for unknown, inactive and live
   * addresses, so callers must NEVER branch their UI on it — always render the same confirmation.
   * Returns the raw envelope rather than throwing so a caller cannot accidentally turn a network
   * error into a signal about the address.
   */
  static async forgotPassword(email: string): Promise<ApiResponse<boolean>> {
    return apiService.post<boolean>('/auth/forgot-password', { email });
  }

  /**
   * Complete a password reset with the token from the emailed link (anonymous).
   *
   * Unlike forgotPassword this one DOES report failures distinguishably — the caller already holds
   * the token, so "invalid or expired link" and "password too weak" are different, actionable
   * outcomes and the UI is expected to tell them apart.
   */
  static async resetPassword(token: string, newPassword: string): Promise<ApiResponse<boolean>> {
    return apiService.post<boolean>('/auth/reset-password', { token, newPassword });
  }

  /**
   * Refresh the access token using the httpOnly refresh cookie (no JS-readable token involved).
   *
   * Tri-state (see {@link RefreshOutcome}) and single-flight across tabs via the Web Locks API, with a
   * bounded exponential-backoff retry on transient failures. It NEVER throws, and it NEVER clears auth
   * on a transient failure — only on a definitive 4xx (≠429) or a 2xx `success:false` rejection.
   *
   * The retry loop lives OUTSIDE the cross-tab lock: each attempt (freshness double-check + at most one
   * network POST) runs inside the lock via {@link withRefreshLock}, but the backoff SLEEP between
   * attempts happens with the lock RELEASED, so a backing-off tab no longer blocks refreshes in other
   * tabs for the whole retry budget.
   *
   * @param maxAttempts caps the transient-retry budget for THIS call. The initial silent refresh on
   *   app load passes {@link INITIAL_REFRESH_MAX_ATTEMPTS} so a passing outage cannot make the load
   *   splash hang for the full budget; every other caller keeps the default full budget.
   */
  static async refreshAccessToken(maxAttempts: number = this.REFRESH_MAX_ATTEMPTS): Promise<RefreshOutcome> {
    // Read the one-time legacy bootstrap token once; the same value is reused across retries (it is
    // purged from localStorage only after a successful refresh — see performSingleRefresh).
    const legacyToken = this.getRefreshToken();
    const attempts = Math.max(1, maxAttempts);

    for (let attempt = 0; attempt < attempts; attempt++) {
      const outcome = await this.withRefreshLock(() => this.performSingleRefresh(legacyToken));

      // 'refreshed' (done) or 'invalid' (definitively dead — auth already cleared): stop immediately.
      if (outcome !== 'transient') {
        return outcome;
      }

      // Transient (503 / other 5xx / 429 / network): back off then retry, UNLESS this was the last
      // attempt. The sleep runs with the lock released so other tabs can make progress meanwhile.
      if (attempt < attempts - 1) {
        await this.refreshBackoffDelay(attempt);
      }
    }

    // Retries exhausted: signal transient WITHOUT clearing auth — the session survives for a later
    // retry (proactive/reactive/init callers must not log the user out on this).
    return 'transient';
  }

  /**
   * Run a single refresh attempt under the cross-tab refresh lock (Web Locks API), serialising it
   * against refreshes in other tabs. The lock is released as soon as the callback resolves, so the
   * caller's backoff sleep is NOT covered by it. Falls back to a module-level single-flight when
   * navigator.locks is unavailable (older browsers / test env) so concurrent in-process callers still
   * coalesce onto one in-flight attempt.
   */
  private static async withRefreshLock(attempt: () => Promise<RefreshOutcome>): Promise<RefreshOutcome> {
    const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
    if (locks && typeof locks.request === 'function') {
      // locks.request resolves to whatever the callback returns; the async wrapper flattens the
      // Promise<Promise<…>> the DOM lib types report back to a single Promise<RefreshOutcome>.
      return locks.request(REFRESH_LOCK_NAME, attempt);
    }

    // Fallback (no Web Locks): coalesce concurrent in-process callers onto one in-flight attempt.
    if (refreshInFlight) {
      return refreshInFlight;
    }
    refreshInFlight = attempt().finally(() => {
      refreshInFlight = null;
    });
    return refreshInFlight;
  }

  /**
   * A SINGLE refresh attempt, run inside the refresh lock. Performs the /auth/refresh POST once. The
   * httpOnly cookie supplies the refresh token, so no body is sent — EXCEPT the one-time legacy
   * bootstrap: if a pre-migration refresh token is still in localStorage it is POSTed once in the body
   * so the server can rotate it into the cookie, then deleted.
   *
   * Post-lock freshness double-check: another flight in THIS tab may have just refreshed — if so AND an
   * access token is actually present, reuse it and skip the network. The freshness timestamp is a
   * NON-secret signal shared across tabs (localStorage), but the in-memory access token is per-tab and
   * does NOT survive a reload; a fresh signal with NO token (a reload, or another tab's refresh) is
   * therefore NOT reusable, so we fall through to a real network refresh instead of falsely reporting
   * 'refreshed' with no token — the bug that bounced a VALID session to the login page on reload/new tab.
   *
   * Returns 'refreshed' on success, 'invalid' on a definitive 4xx (≠429) or a 2xx success:false
   * envelope (auth is cleared on that path only), and 'transient' on 503/5xx/429/network — the caller
   * decides whether to retry. Auth is NEVER cleared on the transient path.
   */
  private static async performSingleRefresh(legacyToken: string | null): Promise<RefreshOutcome> {
    if (this.isRefreshFresh() && this.getToken()) {
      return 'refreshed';
    }

    try {
      const response = await apiService.post<LoginResponse>(
        '/auth/refresh',
        legacyToken ?? undefined,
        { headers: { [REFRESH_HEADER_NAME]: '1' } },
      );

      if (response.success && response.data) {
        this.setToken(response.data.token);
        this.setUser(response.data.user);
        this.markRefreshFresh();
        if (legacyToken) {
          // Migrated: the httpOnly cookie now holds the refresh token — purge the localStorage copy
          // so this bootstrap fires exactly once and no token remains in localStorage.
          this.clearLegacyTokens();
        }
        return 'refreshed';
      }

      // A 2xx envelope with success:false is a definitive rejection, not a transient fault.
      this.clearAuthData();
      return 'invalid';
    } catch (error) {
      if (this.isDefinitiveInvalidError(error)) {
        // Definitive 4xx — the session is dead. Clear and let the caller re-login.
        this.clearAuthData();
        return 'invalid';
      }
      // Transient (503 / other 5xx / 429 / network) — do NOT clear auth; let the caller retry.
      return 'transient';
    }
  }

  /**
   * True for a DEFINITIVE 4xx from /auth/refresh (401 dead cookie, 400 no credential, 403 missing
   * header) — the SPA always sends the header, so 403 should never occur in practice. 429 (rate limit),
   * 5xx and network errors are TRANSIENT and retried.
   */
  private static isDefinitiveInvalidError(error: unknown): boolean {
    const status = (error as { response?: { status?: number } } | null | undefined)?.response?.status;
    return typeof status === 'number' && status >= 400 && status < 500 && status !== 429;
  }

  /** Bounded exponential backoff with jitter. Capped so total wait across attempts stays well under ~8s. */
  private static refreshBackoffDelay(attempt: number): Promise<void> {
    const base = this.refreshBackoffBaseMs * Math.pow(2, attempt);
    const jitter = Math.random() * (this.refreshBackoffBaseMs / 2);
    const delay = Math.min(base + jitter, 3000);
    return new Promise((resolve) => setTimeout(resolve, delay));
  }

  /** Record that a refresh just succeeded (non-secret freshness signal for the cross-flight double-check). */
  private static markRefreshFresh(): void {
    try {
      localStorage.setItem(this.LAST_REFRESH_KEY, Date.now().toString());
    } catch {
      // localStorage unavailable (private mode / quota) — the signal is a best-effort optimisation only.
    }
  }

  /** Whether a successful refresh happened within the freshness window. Reads a timestamp, never a token. */
  private static isRefreshFresh(): boolean {
    try {
      const raw = localStorage.getItem(this.LAST_REFRESH_KEY);
      if (!raw) return false;
      const ts = parseInt(raw, 10);
      if (Number.isNaN(ts)) return false;
      return Date.now() - ts < this.REFRESH_FRESH_WINDOW_MS;
    } catch {
      return false;
    }
  }

  /** Remove any pre-migration localStorage tokens (access + refresh) after a successful bootstrap. */
  private static clearLegacyTokens(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
  }

  /**
   * Check if user is authenticated
   */
  static isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;

    try {
      // Basic token expiration check (you might want to add JWT parsing)
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;

      return payload.exp > currentTime;
    } catch {
      return false;
    }
  }

  /**
   * Check if token needs refresh (within 5 minutes of expiry)
   */
  static shouldRefreshToken(): boolean {
    // NEVER proactively refresh while impersonating (C1): a cookie refresh would mint an admin token and
    // clobber the impersonation identity. The impersonation token is non-refreshable and ends on expiry.
    if (this.isImpersonating()) return false;

    const token = this.getToken();
    if (!token) return false;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      const timeUntilExpiry = payload.exp - currentTime;

      // Refresh if less than 5 minutes until expiry
      return timeUntilExpiry > 0 && timeUntilExpiry < 300;
    } catch {
      return false;
    }
  }

  /**
   * Get the in-memory access token.
   */
  static getToken(): string | null {
    return accessToken;
  }

  /**
   * Set the in-memory access token (never persisted to localStorage — see the module-level note).
   */
  static setToken(token: string): void {
    accessToken = token;
  }

  /**
   * Get the LEGACY localStorage refresh token, if one still exists (pre-migration sessions only). New
   * sessions keep the refresh token in an httpOnly cookie that JS cannot read, so this returns null.
   */
  static getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  /**
   * Seed a legacy localStorage refresh token. Retained for backward compatibility; the production
   * login/verify-2fa/refresh flows no longer call this (the refresh token is a cookie now).
   */
  static setRefreshToken(token: string): void {
    localStorage.setItem(this.REFRESH_TOKEN_KEY, token);
  }

  /**
   * Get stored user data
   */
  static getUser(): UserInfo | null {
    try {
      const userData = localStorage.getItem(this.USER_KEY);
      return userData ? JSON.parse(userData) : null;
    } catch {
      return null;
    }
  }

  /**
   * Set user data
   */
  static setUser(user: UserInfo): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  /**
   * Clear all authentication data — the in-memory access token plus any localStorage residue (legacy
   * tokens, cached user, freshness signal). The httpOnly refresh cookie is cleared server-side by
   * /auth/logout (JS cannot delete it).
   */
  static clearAuthData(): void {
    accessToken = null;
    // Drop any stashed admin token too — a full auth clear (logout / dead session) ends impersonation.
    impersonatorToken = null;
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.LAST_REFRESH_KEY);
  }

  /**
   * Check API health
   */
  static async checkHealth(): Promise<boolean> {
    try {
      const response = await apiService.get('/auth/health');
      return response.success;
    } catch {
      return false;
    }
  }
}

export default AuthService;
